<template>
  <div class="sm-smart-mail">
    <div class="sm-wrap">
      <breadcrumbs-block v-if="!isMobile" :crumbs="breadcrumbs" />
      <div class="sm-smart-mail__top">
        <h2>Insert Mail in Bulk</h2>
        <div class="sm-smart-mail__top-row">
          <input-pmb-tags-multi
            :placeholder="'Name, Mailbox# or Company name'"
            :defaultTags="defaultPMBs"
            :preventDuplicates="true"
            @updateList="handlePMBSelectMulti($event)"
          />
          <btn-block
            v-if="viewPort != 'small'"
            :type="'small'"
            :text="'Insert New Mail Item'"
            :icon="'icon-plus'"
            @click="openNewMail"
          />
        </div>
      </div>

      <div v-if="pmbs.length" class="sm-smart-mail__main">
        <div
          class="sm-smart-mail__main-left"
          :class="{
            scroll: pmbs.length > 1,
          }"
        >
          <smart-mail-tpls
            v-for="pmbItem in pmbs"
            :key="pmbItem.pmb"
            :pmb="pmbItem"
            :mailList="mailList"
            @template="addTemplate"
          />
        </div>
        <div class="sm-smart-mail__main-right">
          <table
            v-if="viewPort != 'small'"
            class="sm-table sm-smart-mail__main-right-table"
          >
            <thead class="sm-table__head">
              <tr class="sm-txt-upper">
                <th class="sm-table__head-item mail-pmb sm-txt-center">
                  Mailbox#
                </th>
                <th class="sm-table__head-item mail-number">ID</th>
                <th class="sm-table__head-item mail-received">Received at</th>
                <th class="sm-table__head-item mail-sender">Sender</th>
                <th class="sm-table__head-item mail-type">Type</th>
                <th class="sm-table__head-item mail-qty">Qty.</th>
                <th class="sm-table__head-item mail-weight">Weight/Oz</th>
                <th class="sm-table__head-item mail-scannable sm-txt-center">
                  Scannable
                </th>
                <th class="sm-table__head-item actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="mailList.length" v-for="item in mailList" :key="item.id">
                <td class="mail-pmb sm-txt-center">
                  {{ item.account_number }}
                </td>
                <td class="mail-number">{{ item.mail_number }}</td>
                <td class="mail-received sm-txt-nowrap">
                  {{ item.received_at | moment("MMM D, YYYY") }}
                </td>
                <td class="mail-sender">{{ item.sender }}</td>
                <td class="mail-type sm-txt-capitalize">
                  <dropdown-wrap
                    :val="
                      mailTypes.find((type) => type.id == item.mail_type_id)
                    "
                    :options="mailTypes"
                    :capitalize="true"
                    @input="setMailType($event, item)"
                  />
                </td>
                <td class="mail-qty">
                  <input-number
                    v-model="item.quantity"
                    :min="1"
                    :defaultVal="item.quantity"
                    :disabled="showSpinner"
                    @input="(q) => handleItemChange({...item, quantity: q})"
                  />
                </td>
                <td class="mail-weight">
                  <input-number
                    v-model="item.weight"
                    :defaultVal="parseInt(item.weight)"
                    :showControls="false"
                    :min="0"
                    @change="handleItemChange(item)"
                  />
                </td>
                <td class="mail-scannable sm-txt-center">
                  <checkbox-block
                    v-model="item.is_scannable"
                    @input="handleItemChange(item)"
                  />
                </td>
                <td class="actions">
                  <div class="sm-smart-mail__main-right-table-actions">
                    <div
                      v-for="action in actions"
                      :key="action.name"
                      class="sm-smart-mail__main-right-table-actions-item"
                    >
                      <a
                        href="#"
                        class="icon"
                        @click.prevent="itemAction(action.name, item)"
                      >
                        <hint-wrap :text="action.text">
                          <template slot="content">
                            <include-svg :name="action.icon" />
                          </template>
                        </hint-wrap>
                      </a>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="sm-smart-mail__main-right-mobile-table">
            <ul
              v-for="item in mailList"
              :key="item.id"
              class="sm-smart-mail__main-right-mobile-table-item"
            >
              <li>
                <span class="sm-txt-upper sm-txt-strong">Mailbox#</span>
                <span class="no-edit">{{ item.account_number }}</span>
              </li>
              <li>
                <span class="sm-txt-upper sm-txt-strong">ID</span>
                <span class="no-edit">{{ item.mail_number }}</span>
              </li>
              <li>
                <span class="sm-txt-upper sm-txt-strong">Received at</span>
                <span class="no-edit">{{
                  item.received_at | moment("MMM D, YYYY")
                }}</span>
              </li>
              <li>
                <span class="sm-txt-upper sm-txt-strong">Sender</span>
                <span class="no-edit">{{ item.sender }}</span>
              </li>
              <li>
                <span class="sm-txt-upper sm-txt-strong">Type</span>
                <dropdown-wrap
                  v-model="item.type"
                  :val="mailTypes.find((type) => type.id == item.mail_type_id)"
                  :options="mailTypes"
                  :capitalize="true"
                  @input="setMailType($event, item)"
                />
              </li>
              <li>
                <span class="sm-txt-upper sm-txt-strong">Weight/lbs</span>
                <input-number
                  v-model="item.weight"
                  :defaultVal="parseInt(item.weight)"
                  :showControls="false"
                  :min="0"
                  @change="handleItemChange(item)"
                />
              </li>
              <li>
                <span class="sm-txt-upper sm-txt-strong">Qty.</span>
                <input-number
                  v-model="item.quantity"
                  :min="1"
                  :defaultVal="item.quantity"
                  :disabled="showSpinner"
                  @input="(q) => handleItemChange({...item, quantity: q})"
                />
              </li>
              <li class="separate">
                <checkbox-block
                  v-model="item.is_scannable"
                  :text="'Scannable'"
                  @input="handleItemChange(item)"
                />
                <div
                  class="sm-smart-mail__main-right-mobile-table-item-actions"
                >
                  <div
                    v-for="action in actions"
                    :key="action.name"
                    class="sm-smart-mail__main-right-table-actions-item"
                  >
                    <a
                      href="#"
                      class="icon"
                      @click.prevent="itemAction(action.name, item)"
                    >
                      <hint-wrap :text="action.text">
                        <template slot="content">
                          <include-svg :name="action.icon" />
                        </template>
                      </hint-wrap>
                    </a>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <!-- <paginate-block
            v-if="paginate.totalPages > 1"
            :page="paginate.page"
            :pages="paginate.totalPages"
            :items="paginate.totalItems"
            :onPage="paginate.limit"
            :morePages="paginate.morePages"
            @showMore="requestMoreBulkMailItems"
          /> -->
        </div>
      </div>
      <div v-else class="sm-smart-mail__empty">
        <div v-if="viewPort != 'small'" class="sm-smart-mail__empty-left"></div>
        <ul class="sm-smart-mail__empty-right">
          <li></li>
          <li></li>
          <li></li>
          <template v-if="viewPort != 'small'">
            <li></li>
            <li></li>
            <li></li>
          </template>
        </ul>
      </div>

      <btn-block
        v-if="viewPort == 'small'"
        :type="'small'"
        :text="'Insert New Mail Item'"
        :icon="'icon-plus'"
        @click="openNewMail"
      />
    </div>

    <div
      class="sm-smart-mail__controls"
      :class="{
        active: mailList.length,
      }"
    >
      <div class="sm-wrap">
        <div class="sm-smart-mail__controls-inner">
          <btn-block
            :text="'Create BulkMail'"
            :class="'sm-btn--small'"
            @click="publishDraftedBulkMailItems"
          />
          <btn-block
            :text="'Discard'"
            :type="'reset'"
            :class="'sm-btn--small'"
            @click="deleteDraftedBulkMailItems"
          />
        </div>
      </div>
    </div>

    <keep-alive>
      <popup-block
        v-if="popup.isOpen"
        :open="popup.isOpen"
        :size="popup.type != 'pmb' ? 'large-600' : 'medium'"
        :class="'sm-smart-mail__popup'"
        @close="popup.isOpen = false"
      >
        <template slot="popupContent">
          <form-new-mail
            v-if="popup.type == 'new' || popup.type == 'edit'"
            :ref="'form'"
            :key="`form-new-mail-${formKey}`"
            :startPMB="pmbs.find(p => popup.item && p.pmb === popup.item.account_number) || pmbs[0] || {}"
            :startVal="popup.type == 'edit' ? popup.item : {}"
            :title="`${popup.type == 'new' ? 'New' : 'Edit'} mail`"
            :type="popup.type == 'edit' ? 'edit' : 'new'"
            @submit="handleFormSubmit"
          />
          <div v-else-if="popup.type == 'pmb'" class="sm-smart-mail__popup-pmb">
            <h4>Select Mailbox#</h4>
            <input-pmb-tags
              :ref="'tags'"
              :placeholder="'Name, Mailbox# or Company name'"
              @updateList="handlePMBSelect($event)"
            />
          </div>
        </template>
      </popup-block>
    </keep-alive>

    <action-notification :add="notif" />

    <sm-spinner :show="showSpinner" />
  </div>
</template>

<script>
import Breadcrumbs from "@/components/Breadcrumbs";
import InputNumber from "@/components/InputNumber";
import Checkbox from "@/components/Checkboxes";
import HintWrap from "@/components/HintWrap";
import DropdownWrap from "@/components/DropdownWrap";
import Popup from "@/components/Popup";
import Spinner from "@/components/Spinner";
import FormNewMail from "@/components/forms/FormNewMail";
import InputPMBTags from "@/components/mail/InputPMBTags";
import InputPMBTagsMulti from "@/components/mail/InputPMBTagsMulti";
import { mapGetters, mapActions } from "vuex";
import vClickOutside from "v-click-outside";
import ViewPort from "@/mixins/ViewPort";
import SmartMailTpls from "@/views/admin/pages/pages-components/smart-mail/Tpls";
import ActionNotification from "@/components/ActionNotification";
import Mail from "@/settings/mail";
import PaginateBlock from "@/components/PaginateBlock";

export default {
  name: "smart-mail",
  components: {
    "breadcrumbs-block": Breadcrumbs,
    "input-number": InputNumber,
    "checkbox-block": Checkbox,
    "hint-wrap": HintWrap,
    "dropdown-wrap": DropdownWrap,
    "popup-block": Popup,
    "form-new-mail": FormNewMail,
    "sm-spinner": Spinner,
    "input-pmb-tags": InputPMBTags,
    "input-pmb-tags-multi": InputPMBTagsMulti,
    "smart-mail-tpls": SmartMailTpls,
    "action-notification": ActionNotification,
    "paginate-block": PaginateBlock,
  },
  data() {
    return {
      breadcrumbs: [
        {
          link: "mail?tab=single",
          text: "Mail Single",
        },
        {
          link: "/smart-mail",
          text: "Insert Mail in Bulk",
          current: true,
        },
      ],
      defaultPMBs: [],
      pendingRequests: new Map(),
      pmbs: [],
      mailList: [],
      // paginate: {
      //   page: 1,
      //   limit: 8,
      //   totalPages: 0,
      //   totalItems: 0,
      //   morePages: 0,
      // },
      actions: [
        {
          name: "edit",
          icon: "icon-settings-edit",
          text: "Edit mail item",
        },
        {
          name: "remove",
          icon: "icon-settings-remove",
          text: "Remove mail item",
        },
      ],
      popup: {
        isOpen: false,
        type: "", // values: new, edit, pmb
        item: null,
      },
      showSpinner: false,
      allowedBreakpoints: ["small", "large"],
      formKey: 0,
      notif: null,
      notifCount: 0,

      savedData: {},
      nextMailNumber: null,
    };
  },
  computed: {
    ...mapGetters({
      isMobile: "isMobile",
      mailTypes: "mailTypes",
    }),
  },
  created() {
    this.showSpinner = true;
    
    // let query = this.$route.query;
    
    // if(!query.page || !query.limit) 
    //   this.$router.push({query: {
    //   page: query.page || 1 , 
    //   limit: query.limit || 8
    // }});

    let lsData = localStorage.getItem("saved-smart-mail");
    if (lsData) this.savedData = JSON.parse(lsData);

    Promise.all([
      this.getMailTypes(),
      this.getAdminList({
        // limit: this.paginate.limit,
        // page: this.paginate.page,
        is_draft: true,
      }),
    ]).then(([mail_types, mail_list]) => {
      this.mailList = mail_list.mail_items;
      // this.paginate.totalItems = mail_list.total_count;
      // this.paginate.totalPages = mail_list.total_pages;

      if (!this.savedData.pmbs) {
        this.popup.type = "pmb";
        this.popup.isOpen = true;

        setTimeout(() => {
          if (this.$refs.tags && this.$refs.tags.$el)
            this.$refs.tags.$el.querySelector("input").focus();
        }, 100);
      } else {
        this.pmbs = this.savedData.pmbs;
        this.defaultPMBs = [...this.pmbs];
      }

      this.showSpinner = false;
    });
  },
  mixins: [ViewPort],
  methods: {
    ...mapActions({
      getAdminList: "mail/getAdminList",
      removeBulkMail: "mail/removeBulkMail",
      getMailTypes: "getMailTypes",
      findPMBs: "mail/findPMBs",
      createBulkMail: "mail/createBulkMail",
      editMail: "mail/editMail",
      publishBulkMail: "mail/publishBulkMail",
    }),
    async callDeduped(method,...args) {
      const argsStr = JSON.stringify(args);
      const key = `${method}_${argsStr}`;
      if (this.pendingRequests.has(key)) {
        // Return the existing promise if request is in progress
        return this.pendingRequests.get(key);
      }
      // Create a new request
      const requestPromise = this[method](...args)
        .finally(() => {
          // Remove from pending once done
          this.pendingRequests.delete(key);
        });

      // Store in map
      this.pendingRequests.set(key, requestPromise);

      return requestPromise;
    },
    handlePMBSelect(data) {
      if (!data.length) return;

      this.showSpinner = true;
      let pmb_number = data[0];

      this.findPMBs(pmb_number).then((res) => {
        this.popup.isOpen = false;
        this.showSpinner = false;

        this.defaultPMBs = [res.find((item) => item.pmb == pmb_number)];
      });
    },
    handlePMBSelectMulti(data) {
      if (!data.length) {
        localStorage.removeItem("saved-smart-mail");
        return;
      }

      this.pmbs = data;
    },
    addTemplate(template) {
      this.createBulkMailDraft(template);
    },
    updateTemplate(data) {
      this.updateDraftedBulkMailItem(data);
    },
    openNewMail() {
      if (this.popup.type != "new") this.formKey++;

      this.popup.type = "new";
      this.popup.isOpen = true;

      setTimeout(() => {
        if (this.$refs.form && this.$refs.form.$el)
          this.$refs.form.$el
            .querySelector(".sm-input-tags-wrap input")
            .focus();
      }, 100);
    },
    handleFormSubmit(formData) {
      this.formKey++;
      if (this.popup.type == "new") this.addTemplate(formData);
      else if (this.popup.type == "edit") this.updateTemplate(formData);

      this.popup.isOpen = false;
    },
    itemAction(action, item) {
      switch (action) {
        case "edit":
          this.formKey++;
          this.popup.type = "edit";
          this.popup.item = item;
          this.popup.isOpen = true;
          break;
        case "remove":
          this.deleteDraftedBulkMailItems([item.id]);
          let index = this.mailList.findIndex((tpl) => tpl == item);
          this.mailList.splice(index, 1);
          break;
        default:
          break;
      }
    },
    setMailType(event, item) {
      item.mail_type_id = event.id;
      item.is_scannable = typeof item.is_scannable == "boolean" ? item.is_scannable : Mail.defaultScannableTypes.includes(
        event.name.toLowerCase()
      );
      this.updateDraftedBulkMailItem(item);
    },
    createBulkMailDraft(item) {
      this.showSpinner = true;
      let items = [
        {
          sender: item.sender,
          mail_type_id: item.mail_type_id,
          quantity: item.quantity,
          weight: item.weight,
          weight_unit: item.weight_unit || "oz",
          tracking: item.tracking,
          is_scannable: item.is_scannable,
          account_number: item.account_number,
          is_draft: true,
        }
      ];

      this.createBulkMail(items).then((res) => {
        if (res.created) this.showSpinner = false;
        return this.listDraftedBulkMailItems(
          // { page: 1 }
        );
      });
    },
    publishDraftedBulkMailItems() {
      this.showSpinner = true;
      const ids = this.mailList.map((item) => item.id);
      if (ids.length) {
        const params = { mail_item_ids: ids };
        this.publishBulkMail(params).then((res) => {
          this.showSpinner = false;
          this.notifCount++;
          this.notif = {
            id: `smart-mail-${this.notifCount}`,
            text: `Created ${ids.length} mail item${ids.length > 1 ? "s" : ""}`,
          };
          this.mailList = [];
        });
      }
    },
    listDraftedBulkMailItems(params, isShowMore) {
      this.showSpinner = true;

      // if (isShowMore) this.paginate.morePages++;
      // else this.paginate.morePage = 0;
      
      this.getAdminList(
      {
        is_draft: true,
      //   limit: this.paginate.limit,
      //   page: this.paginate.page + this.paginate.morePages,
      //   ...params,
      }
      
      ).then((res) => {
        this.mailList = res.mail_items;
        // if (isShowMore) {
        //   this.morePages++;
        //   this.mailList = [...this.mailList, ...res.mail_items];
        // } else {
        //   this.mailList = res.mail_items;
        // }
        // if(!isShowMore) this.paginate.page = res.page;
        // this.paginate.totalItems = res.total_count;
        // this.paginate.totalPages = res.total_pages;
        this.showSpinner = false;
      });
    },
    requestMoreBulkMailItems() {
      this.listDraftedBulkMailItems({}, true);
    },
    updateDraftedBulkMailItem(item) {
      this.showSpinner = true;
      const toSend = {
        id: item.id,
        params: item,
      };
      this.callDeduped("editMail", toSend).then((res) => {
        const item = res.mail_item;
        this.popup.item = item;
        const newList = [...this.mailList];
        let index = newList.findIndex((el) => el.id == item.id);
        newList[index] = item;
        this.mailList = newList;
        this.showSpinner = false;
      });
    },
    deleteDraftedBulkMailItems(idsArr) {
      this.showSpinner = true;
      const ids = idsArr ? idsArr : this.mailList.map((item) => item.id);
      const params = { mail_item_ids: ids };
      this.removeBulkMail(params).then((res) => {
        this.showSpinner = false;
        return this.listDraftedBulkMailItems({ page: 1 });
      });
    },
    debounce(func, timeout = 300){
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => { func.apply(this, args); }, timeout);
      };
    },
    handleItemChange(item) {
      this.debounce(this.updateTemplate(item), 500)
    },
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  watch: {
    // "$route.query": {
    //   immediate: true,
    //   handler(val) {
    //     this.paginate = {...this.paginate, page: Number(val.page || 1), limit: Number(val.limit || 8)}
    //     this.listDraftedBulkMailItems(val, false);
    //   }
    // },
    pmbs(val) {
      this.savedData.pmbs = val;

      if (val && Array.isArray(val))
        localStorage.setItem(
          "saved-smart-mail",
          JSON.stringify(this.savedData)
        );
    },
  },
};
</script>

<style lang="scss">
@use "@/assets/scss/general/mixins.scss" as *;
@use "@/assets/scss/framework/scss-variables.scss" as *;
@use "@/assets/scss/pages/common/_smart-mail.scss";
</style>